import { differenceInCalendarDays } from "date-fns";

const HUF = new Intl.NumberFormat("hu-HU", {
  style: "currency",
  currency: "HUF",
  maximumFractionDigits: 0,
});

export const formatHuf = (amount: number): string =>
  HUF.format(Number.isFinite(amount) ? amount : 0);

export const nightsBetween = (
  from: string | Date | null | undefined,
  to: string | Date | null | undefined
): number => {
  if (!from || !to) return 0;
  const fromDate = from instanceof Date ? from : new Date(from);
  const toDate = to instanceof Date ? to : new Date(to);
  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return 0;
  const nights = differenceInCalendarDays(toDate, fromDate);
  return nights > 0 ? nights : 0;
};

export type OfferLineTotals = {
  nights: number;
  /** What we pay the accommodation for the whole line. */
  cost: number;
  /** What the customer is charged (net) for the whole line. */
  revenue: number;
  /** Margin per person per night. */
  marginPerPersonPerNight: number;
  /** Margin for the whole line. */
  margin: number;
};

export type OfferLineInput = {
  from: string | Date | null | undefined;
  to: string | Date | null | undefined;
  numberOfPeople: number | string | null | undefined;
  grossPricePerPersonPerNight: number | string | null | undefined;
  retailPrice: number | string | null | undefined;
};

export const lineTotals = (item: OfferLineInput): OfferLineTotals => {
  const nights = nightsBetween(item.from, item.to);
  const people = Number(item.numberOfPeople) || 0;
  const gross = Number(item.grossPricePerPersonPerNight) || 0;
  const retail = Number(item.retailPrice) || 0;
  const units = nights * people;

  return {
    nights,
    cost: gross * units,
    revenue: retail * units,
    marginPerPersonPerNight: retail - gross,
    margin: (retail - gross) * units,
  };
};

export const offerTotals = (
  items: OfferLineInput[]
): Omit<OfferLineTotals, "marginPerPersonPerNight" | "nights"> & {
  marginPercent: number;
} => {
  const totals = items.reduce(
    (acc, item) => {
      const line = lineTotals(item);
      acc.cost += line.cost;
      acc.revenue += line.revenue;
      acc.margin += line.margin;
      return acc;
    },
    { cost: 0, revenue: 0, margin: 0 }
  );

  return {
    ...totals,
    marginPercent:
      totals.revenue > 0 ? (totals.margin / totals.revenue) * 100 : 0,
  };
};
